<script>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import { useCartGLobalStore } from '@/stores/cartGlobalStore'
import { useThemeStore } from '@/stores/themeStore'
import { createMollieSessionFn, placeOrderMollieSessionFn } from '@/services/api/restApiCalls'

const MOLLIE_V2_SCRIPT_ID = 'mollie-js-v2'

function loadMollieV2Script() {
  return new Promise((resolve, reject) => {
    if (window.MollieCheckout) {
      resolve(window.MollieCheckout)
      return
    }
    const existing = document.getElementById(MOLLIE_V2_SCRIPT_ID)
    if (existing) {
      existing.addEventListener('load', () => resolve(window.MollieCheckout))
      existing.addEventListener('error', reject)
      return
    }
    const script = document.createElement('script')
    script.id = MOLLIE_V2_SCRIPT_ID
    script.src = 'https://js.mollie.com/v2/mollie.js'
    script.async = true
    script.onload = () => resolve(window.MollieCheckout)
    script.onerror = reject
    document.head.appendChild(script)
  })
}

export default {
  props: {
    beforePlaceOrder: { type: Function, default: null }
  },

  emits: ['pay-button-state'],

  setup(props, { expose, emit }) {
    const router = useRouter()
    const restoStore = useRestoGLobalStore()
    const cartStore = useCartGLobalStore()
    const themeStore = useThemeStore()

    const cardErrorMsg = ref('')
    const payButtonDisabled = ref(true)
    const isLoading = ref(true)
    const isProcessing = ref(false)
    const isUnmounted = ref(false)
    const isDark = computed(() => themeStore.theme === 'dark')
    let cardComponent = null
    let checkout = null

    const formattedAmount = computed(() => {
      const total = cartStore.CART_DATA?.order_total || 0
      return `£${Number(total).toFixed(2)}`
    })

    const finalizeOrder = async () => {
      const response = await placeOrderMollieSessionFn(restoStore?.RESTO_ID)
      if (!response || response?.status >= 400 || response?.error_code) {
        cardErrorMsg.value = response?.status_msg || 'Order creation failed.'
        isProcessing.value = false
        payButtonDisabled.value = false
        return
      }
      isProcessing.value = false
      router.push({
        name: 'order_status',
        params: { order_id: `${response.order_id}` }
      })
      cartStore.CART_DATA = ''
    }

    const initSession = async () => {
      try {
        const session = await createMollieSessionFn(restoStore?.RESTO_ID)
        if (!session || session?.status >= 400 || !session?.client_access_token) {
          cardErrorMsg.value = session?.status_msg || 'Unable to initialize payment.'
          isLoading.value = false
          payButtonDisabled.value = true
          return
        }

        await loadMollieV2Script()
        checkout = window.MollieCheckout(session.client_access_token, { locale: 'en_GB' })
        cardComponent = checkout.create('card')
        cardComponent.mount(document.getElementById('mollie-card-component-v2'))

        try {
          const applePayEl = document.getElementById('mollie-applepay-component-v2')
          if (applePayEl && checkout.create) {
            const applePayComponent = checkout.create('applepay')
            if (applePayComponent?.mount) {
              applePayComponent.mount(applePayEl)
              applePayComponent.on('complete', async () => {
                if (isUnmounted.value) return
                isProcessing.value = true
                payButtonDisabled.value = true
                if (props.beforePlaceOrder) {
                  const ready = await props.beforePlaceOrder()
                  if (!ready) {
                    isProcessing.value = false
                    payButtonDisabled.value = false
                    return
                  }
                }
                await finalizeOrder()
              })
            }
          }
        } catch (_) {
          // Apple Pay not enabled on profile or unsupported browser
        }

        cardComponent.on('complete', async () => {
          if (isUnmounted.value) return
          isProcessing.value = true
          payButtonDisabled.value = true
          if (props.beforePlaceOrder) {
            const ready = await props.beforePlaceOrder()
            if (!ready) {
              isProcessing.value = false
              payButtonDisabled.value = false
              return
            }
          }
          await finalizeOrder()
        })

        cardComponent.on('error', (error) => {
          cardErrorMsg.value = error?.message || 'Payment failed.'
          isProcessing.value = false
          payButtonDisabled.value = false
        })

        isLoading.value = false
        payButtonDisabled.value = false
      } catch (err) {
        cardErrorMsg.value = 'Unable to load Mollie payment form.'
        isLoading.value = false
        payButtonDisabled.value = true
      }
    }

    const handleCardPayment = async () => {
      if (payButtonDisabled.value || isProcessing.value) return
      cardErrorMsg.value = 'Complete payment using the card form above.'
    }

    onMounted(() => {
      isUnmounted.value = false
      initSession()
    })

    onBeforeUnmount(() => {
      isUnmounted.value = true
      if (cardComponent?.unmount) {
        cardComponent.unmount()
      }
    })

    watch(
      payButtonDisabled,
      (disabled) => {
        emit('pay-button-state', disabled !== false)
      },
      { immediate: true }
    )

    expose({
      handleCardPayment,
      handleStripePayment: handleCardPayment,
      handleMolliePayment: handleCardPayment,
      payButtonDisabled
    })

    return {
      cardErrorMsg,
      payButtonDisabled,
      isLoading,
      isProcessing,
      isDark,
      formattedAmount,
      handleCardPayment
    }
  }
}
</script>

<template>
  <div class="mollie-payment-element-wrapper" :class="isDark ? 'mollie-theme-dark' : 'mollie-theme-light'">
    <div v-if="isLoading" class="p-4 text-center text-gray-500 dark:text-gray-400">
      Loading payment details...
    </div>
    <label v-show="!isLoading" class="block text-sm font-medium text-gray-900 dark:text-gray-300">Card details</label>
    <div
      id="mollie-applepay-component-v2"
      class="mollie-applepay-component mt-1"
      :class="{ 'mollie-hidden': isLoading }"
    ></div>
    <div
      id="mollie-card-component-v2"
      class="mollie-card-component"
      :class="{ 'mollie-hidden': isLoading }"
    ></div>
    <div v-if="cardErrorMsg" class="text-sm font-medium text-red-600 dark:text-red-400 mt-2 lblError">
      {{ cardErrorMsg }}
    </div>
    <p v-if="!isLoading" class="text-xs text-gray-500 dark:text-gray-400 mt-2">
      Pay {{ formattedAmount }} securely by card. Apple Pay may appear during bank verification.
    </p>
  </div>
</template>

<style>
.mollie-payment-element-wrapper {
  position: relative;
  width: 100%;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

.mollie-hidden {
  display: none;
}

.mollie-applepay-component:empty {
  display: none;
}

.mollie-applepay-component:not(:empty) {
  min-height: 44px;
  margin-bottom: 10px;
}

.mollie-card-component {
  border-radius: 0.375rem;
  padding: 12px;
  border: 1px solid #d1d5db;
  min-height: 44px;
  width: 100%;
  background: #ffffff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}

.mollie-theme-dark .mollie-card-component,
html.dark .mollie-payment-element-wrapper .mollie-card-component {
  background: #374151;
  border-color: #4b5563;
  color: #e5e7eb;
}

.mollie-card-component:focus-within {
  border-color: #6366f1;
  box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.5);
}

.mollie-theme-dark .mollie-card-component:focus-within,
html.dark .mollie-payment-element-wrapper .mollie-card-component:focus-within {
  border-color: #818cf8;
  box-shadow: 0 0 0 1px rgba(129, 140, 248, 0.5);
}

.mollie-payment-element-wrapper .lblError {
  margin: 0;
  min-height: 20px;
}
</style>
